/** Projections over code refs. */

import { projectInstalledCapabilities } from '../apps/reads'
import type {
  CodeRef,
  SessionCheckout,
  SessionCodeRef,
  SessionSourceCheckout,
} from './types'

/**
 * The vcs capability's doc id (RFC 0264 &sect;4), or null if vcs is not installed.
 * The one lookup a host needs before hydrating the `VcsDoc` to read the code
 * pointer (`projectCode`).
 */
export function vcsCapabilityDocId(projectDoc: unknown): string | null {
  const vcs = projectInstalledCapabilities(projectDoc).find(
    (c) => c.name === 'vcs',
  )
  return vcs ? vcs.docId : null
}
/**
 * Project the code pointer from a `VcsDoc` snapshot (RFC 0264 &sect;2). The two-doc
 * read: resolve the vcs capability's doc id off the project
 * (`vcsCapabilityDocId`), hydrate that `VcsDoc`, then read it here. `null` for a
 * snapshot with no valid `code`, so a caller renders no code surface rather than
 * throwing.
 */
export function projectCode(vcsDoc: unknown): CodeRef | null {
  if (!vcsDoc || typeof vcsDoc !== 'object') return null
  const code = (vcsDoc as { code?: unknown }).code
  if (!code || typeof code !== 'object') return null
  const c = code as Partial<CodeRef>
  if (typeof c.trunk !== 'string' || typeof c.headCommit !== 'string') {
    return null
  }
  const out: CodeRef = {
    vcs: c.vcs === 'jj' ? 'jj' : 'git',
    trunk: c.trunk,
    headCommit: c.headCommit,
  }
  if (typeof c.remote === 'string') out.remote = c.remote
  const blob = (c.treeBlob ?? undefined) as { hash?: unknown } | undefined
  if (blob && typeof blob.hash === 'string') out.treeBlob = { hash: blob.hash }
  return out
}
/**
 * Project a session doc's code ref (RFC 0264 &sect;2): the branch it works on and
 * where its head sits. `null` for a session that never touched code. What a review
 * reads to know a session's `(branch, head)` for the diff.
 */
export function projectSessionCode(sessionDoc: unknown): SessionCodeRef | null {
  if (!sessionDoc || typeof sessionDoc !== 'object') return null
  const code = (sessionDoc as { code?: unknown }).code
  if (!code || typeof code !== 'object') return null
  const c = code as Partial<SessionCodeRef>
  if (
    typeof c.branch !== 'string' ||
    typeof c.baseCommit !== 'string' ||
    typeof c.headCommit !== 'string'
  ) {
    return null
  }
  return {
    vcs: c.vcs === 'jj' ? 'jj' : 'git',
    ...(typeof c.sourceId === 'string' ? { sourceId: c.sourceId } : {}),
    branch: c.branch,
    baseCommit: c.baseCommit,
    headCommit: c.headCommit,
  }
}

function checkoutOf(value: unknown): SessionCheckout | null {
  if (!value || typeof value !== 'object') return null
  const c = value as Record<string, unknown>
  // Off the log a string may be an ImmutableString: present, then stringified.
  if (c.branch == null || c.baseCommit == null || c.headCommit == null)
    return null
  return {
    vcs: String(c.vcs) === 'jj' ? 'jj' : 'git',
    branch: String(c.branch),
    baseCommit: String(c.baseCommit),
    headCommit: String(c.headCommit),
  }
}

/**
 * Every source a session has branched (RFC 0362 3b), as project, source and
 * checkout. A source not listed is its shared checkout.
 *
 * An older session holds one `code` record instead. It reads as one entry in
 * its home project, under `codeSourceId` (or the source `code` names, or the
 * project's default source): the migration is this read, so no document is
 * rewritten. A `checkouts` entry for the same source wins.
 */
export function sessionCheckouts(
  sessionDoc: unknown,
  home: { projectId: string; defaultSourceId: string },
): SessionSourceCheckout[] {
  if (!sessionDoc || typeof sessionDoc !== 'object') return []
  const doc = sessionDoc as {
    checkouts?: Record<string, Record<string, unknown>>
    codeSourceId?: unknown
  }
  const out: SessionSourceCheckout[] = []
  const seen = new Set<string>()
  for (const [projectId, sources] of Object.entries(doc.checkouts ?? {})) {
    for (const [sourceId, value] of Object.entries(sources ?? {})) {
      const checkout = checkoutOf(value)
      if (!checkout) continue
      out.push({ projectId, sourceId, checkout })
      seen.add(`${projectId}\u0000${sourceId}`)
    }
  }
  const legacy = projectSessionCode(sessionDoc)
  if (legacy) {
    // `codeSourceId` first, as placement and the header read it: a source
    // move rewrites one of the two, and reading them in different orders
    // filed the branch under a source the cwd was no longer in.
    const sourceId =
      (doc.codeSourceId != null ? String(doc.codeSourceId) : undefined) ??
      legacy.sourceId ??
      home.defaultSourceId
    if (!seen.has(`${home.projectId}\u0000${sourceId}`))
      out.push({
        projectId: home.projectId,
        sourceId,
        checkout: {
          vcs: legacy.vcs,
          branch: legacy.branch,
          baseCommit: legacy.baseCommit,
          headCommit: legacy.headCommit,
        },
      })
  }
  return out
}

/** The checkout a session uses for one source, or null for the shared one. */
export function sessionCheckoutFor(
  sessionDoc: unknown,
  home: { projectId: string; defaultSourceId: string },
  projectId: string,
  sourceId: string,
): SessionCheckout | null {
  return (
    sessionCheckouts(sessionDoc, home).find(
      (entry) => entry.projectId === projectId && entry.sourceId === sourceId,
    )?.checkout ?? null
  )
}
